import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Send } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Newsletters() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", subject: "", intro: "" });

  const { data: newsletters } = useQuery({
    queryKey: ["admin-newsletters"],
    queryFn: async () => (await api.get("/admin/newsletters")).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-newsletters"] });

  const create = async () => {
    try {
      await api.post("/admin/newsletters", { ...form, auto_fill: true });
      toast.success("Digest built from the 5 latest published stories");
      setOpen(false);
      setForm({ title: "", subject: "", intro: "" });
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async (id: string) => {
    try {
      const { data } = await api.post(`/admin/newsletters/${id}/send`);
      toast.success(data.message);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-newsletters-page">
      <PageHeader title="Newsletters" sub="Auto-build digests from the latest automated content and send to subscribers"
        actions={<Button onClick={() => setOpen(true)} data-testid="create-newsletter-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]"><Plus className="h-4 w-4 mr-1" /> Build digest</Button>} />

      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-5">
        <p className="text-yellow-400 text-xs font-mono">
          ⚠ Email delivery is currently MOCKED (logged only). Add your Resend API key to enable real sends.
        </p>
      </div>

      <Table headers={["Newsletter", "Stories", "Status", "Recipients", "Created", "Actions"]}>
        {(newsletters || []).map((n: any) => (
          <tr key={n.id} data-testid={`newsletter-row-${n.id}`}>
            <td className="px-4 py-2.5">
              <p className="text-white">{n.title}</p>
              <p className="font-mono text-[10px] text-gray-600">{n.subject}</p>
            </td>
            <td className="px-4 py-2.5 font-mono text-gray-400">{(n.article_ids || []).length}</td>
            <td className="px-4 py-2.5"><StatusBadge status={n.status} /></td>
            <td className="px-4 py-2.5 font-mono text-gray-400">{n.recipients_count || "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(n.created_at)}</td>
            <td className="px-4 py-2.5">
              {n.status === "draft" && (
                <Button size="sm" onClick={() => send(n.id)} data-testid={`newsletter-send-${n.id}`}
                  className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25">
                  <Send className="h-3.5 w-3.5 mr-1" /> Send
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {(newsletters || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-4">No newsletters yet. Build your first digest — it auto-fills with the latest stories.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">Build newsletter digest</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Internal title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Daily Digest — June 12" data-testid="newsletter-form-title"
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Email subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Today in crypto: ..." data-testid="newsletter-form-subject"
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Intro (optional)</Label>
              <Textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })}
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <p className="text-gray-500 text-xs">The 5 most recent published stories will be attached automatically.</p>
            <Button onClick={create} disabled={!form.title || !form.subject} data-testid="newsletter-form-save"
              className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Build digest</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
